import { type ActionEvent, Controller } from "@hotwired/stimulus"
import Supercluster, { type AnyProps, type ClusterFeature, type PointFeature } from "supercluster"

import LoaderController from "../loader_controller"

import { pluralize } from "@/helpers/pluralize"
import {
  type AdvancedMarkerElement,
  type MapPin,
  ACTIVE_CLASS,
  HIGHLIGHTED_CLASS,
  Z_INDEX,
  cardContent,
  clusterMarkerContent,
  markerKey,
  pinMarkerContent,
  pointsFromPins,
  setSearchBarHeightForCSS,
  toggleMarkerState,
  urlWithBounds,
  validBounds
} from "@/helpers/search_map"
import { selectContentEvent } from "@/helpers/ga4_helper"
import { sessionStorage } from "@/helpers/storage"
import { renderTurboStream } from "@/helpers/turbo"
import { debounce } from "@/helpers/utils"

const MAP_OPEN_CLASS = "--map-open"
const RESULTS_COLLAPSED_CLASS = "--results-collapsed"
const SESSION_STORAGE_KEYS = Object.freeze({
  MAP_OPEN: "search.map.map_open",
  RESULTS_COLLAPSED: "search.map.results_collapsed"
} as const)

export default class SearchMapController extends Controller {
  static outlets = ["loader"]
  declare readonly loaderOutlet: LoaderController

  static targets = ["map", "mapPins", "markerContent"]
  declare readonly mapTarget: HTMLElement
  declare readonly markerContentTarget: HTMLElement

  static values = {
    mapCenter: Object,
    mapOpen: { type: Boolean, default: false },
    resultsCollapsed: { type: Boolean, default: false }
  }
  declare mapCenterValue: { lat: number; lng: number }
  declare mapOpenValue: boolean
  declare resultsCollapsedValue: boolean

  fetchMarkersDebounce = debounce(this.fetchMarkers.bind(this), 100)
  onViewportChangeDebounce = debounce(this.onViewportChange.bind(this), 250)

  declare private static LatLngBounds: typeof google.maps.LatLngBounds
  declare private static MarkerLibrary: google.maps.MarkerLibrary

  private activeMarker: AdvancedMarkerElement | null = null
  declare private googleMap: google.maps.Map
  private isMapPanned = window.mapData.isMapPanned
  private lookupAbort = new window.AbortController()
  private markers: Map<string, AdvancedMarkerElement> = new Map()
  declare private supercluster: Supercluster

  initialize(): void {
    if (sessionStorage) {
      this.mapOpenValue = sessionStorage.getItem(SESSION_STORAGE_KEYS.MAP_OPEN) !== null
      this.resultsCollapsedValue =
        sessionStorage.getItem(SESSION_STORAGE_KEYS.RESULTS_COLLAPSED) !== null
    }
  }

  connect(): void {
    setSearchBarHeightForCSS()
  }

  disconnect(): void {
    this.fetchMarkersDebounce.cancel()
    this.onViewportChangeDebounce.cancel()
    this.lookupAbort.abort()

    if (this.googleMap) {
      window.google.maps.event.clearInstanceListeners(this.googleMap)
    }
  }

  mapPinsTargetConnected(): void {
    this.isMapPanned = window.mapData.isMapPanned
    this.syncMarkers()
  }

  async mapOpenValueChanged(): Promise<void> {
    if (this.mapOpenValue) {
      if (!this.googleMap) {
        await this.initGoogleMap()
      }

      window.scrollTo(0, 0)
      setSearchBarHeightForCSS()
      document.body.classList.add(MAP_OPEN_CLASS)

      sessionStorage?.setItemAtUserRequest(SESSION_STORAGE_KEYS.MAP_OPEN, "")

      this.mapTarget.hidden = false
    } else {
      this.deactivateMarker()
      document.body.classList.remove(MAP_OPEN_CLASS, RESULTS_COLLAPSED_CLASS)

      sessionStorage?.removeItem(SESSION_STORAGE_KEYS.MAP_OPEN)

      this.mapTarget.hidden = true
    }

    this.loaderOutlet.stop()
  }

  resultsCollapsedValueChanged(): void {
    // Toggle class on body instead of relevant element to prevent it getting removed by
    // Turbo stream response.
    document.body.classList.toggle(RESULTS_COLLAPSED_CLASS, this.resultsCollapsedValue)

    if (this.resultsCollapsedValue) {
      sessionStorage?.setItemAtUserRequest(SESSION_STORAGE_KEYS.RESULTS_COLLAPSED, "")
    } else {
      sessionStorage?.removeItem(SESSION_STORAGE_KEYS.RESULTS_COLLAPSED)
    }
  }

  deactivateMarker(): void {
    if (this.activeMarker) {
      toggleMarkerState(this.activeMarker, ACTIVE_CLASS, false)

      this.markerContentTarget.textContent = ""
      this.activeMarker = null
    }
  }

  hide(): void {
    this.mapOpenValue = false
    this.resultsCollapsedValue = false
  }

  get mapBounds(): google.maps.LatLngBounds | undefined {
    // This function called when applying filters, which can obviously happen before map is initialized.
    return this.googleMap?.getBounds()
  }

  syncMarkers(): void {
    // This function is also called on `#turbo-filters-*` frame loads, which can obviously happen
    // before the map is ever initialized.
    if (!this.googleMap) {
      return
    }

    // Refresh supercluster and update markers.
    this.supercluster.load(pointsFromPins())
    this.recreateMarkers()

    // Fit bounds to center the map on the new markers, but not if user panned map.
    if (this.markers.size && !this.isMapPanned) {
      // The `fitBounds` method automatically adjusts zoom level which may force clusters
      // to expand or collapse, so update markers again after next idle.
      window.google.maps.event.addListenerOnce(this.googleMap, "idle", () => {
        this.recreateMarkers()
      })

      this.googleMap.fitBounds(this.boundsForAllPoints)
    }
  }

  show(): void {
    this.mapOpenValue = true
  }

  toggleHighlightMarker({ params: { venueId } }: ActionEvent): void {
    const marker = this.markers.get(`pin:${venueId}`)

    if (marker) {
      toggleMarkerState(marker, HIGHLIGHTED_CLASS)
    }
  }

  toggleMap(): void {
    const openingMap = !this.mapOpenValue

    this.resultsCollapsedValue = false
    this.mapOpenValue = openingMap
  }

  toggleResultsList(): void {
    this.resultsCollapsedValue = !this.resultsCollapsedValue
  }

  zoomIn(): void {
    this.googleMap.setZoom(this.googleMap.getZoom()! + 1)
  }

  zoomOut(): void {
    this.googleMap.setZoom(this.googleMap.getZoom()! - 1)
  }

  private activateMarker(marker: AdvancedMarkerElement, pin: MapPin): void {
    // Deactivate any current active marker.
    this.deactivateMarker()

    this.markerContentTarget.appendChild(cardContent(pin))
    toggleMarkerState(marker, ACTIVE_CLASS, true)

    this.activeMarker = marker
  }

  private appendCustomControls(): void {
    const sharedClasses =
      "bg-white rounded-8 shadow-[0_1px_4px_-1px_color-mix(in_srgb,var(--color-black),70%_transparent)] text-base"

    const template = document.createElement("template")
    template.innerHTML = `
      <div class="flex flex-col items-end px-10 mt-10 mr-10 gap-8">
        <button
          type="button"
          class="${sharedClasses} hidden lg:flex items-center p-10"
          data-action="click->search-map#hide"
          data-ga4-click="click"
          data-ga4-event-group="Controls"
          data-ga4-click-target="Map Collapse"
        >
          <i class="far fa-xmark fa:before" aria-hidden="true"></i> Hide map
        </button>
        <div class="${sharedClasses} flex flex-col divide-y px-10">
          <button type="button" class="py-10 border-secondary" data-action="click->search-map#zoomIn">
            <span class="sr-only">Zoom in</span>
            <i class="fas fa-plus fa-fw" aria-hidden="true"></i>
          </button>
          <button type="button" class="py-10" data-action="click->search-map#zoomOut">
            <span class="sr-only">Zoom out</span>
            <i class="fas fa-minus fa-fw" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    `

    this.googleMap.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(
      template.content.firstElementChild! as HTMLElement
    )
  }

  private get boundsForAllPoints(): google.maps.LatLngBounds {
    const worldBounds = SearchMapController.LatLngBounds.MAX_BOUNDS
    const allPoints = this.pointsInBounds(worldBounds, this.googleMap.get("maxZoom"))
    const bounds = new SearchMapController.LatLngBounds()

    for (const point of allPoints) {
      const [lng, lat] = point.geometry.coordinates
      bounds.extend({ lng, lat })
    }

    return bounds
  }

  private createClusterMarker(key: string, cluster: ClusterFeature<AnyProps>): void {
    if (this.markers.has(key)) {
      return
    }

    const [lng, lat] = cluster.geometry.coordinates
    const count = cluster.properties.point_count
    const marker = new SearchMapController.MarkerLibrary.AdvancedMarkerElement({
      content: clusterMarkerContent(count),
      map: this.googleMap,
      position: { lat, lng },
      title: `${pluralize(count, "spa")} in this area`,
      zIndex: Z_INDEX.CLUSTER
    }) as AdvancedMarkerElement

    marker.addListener("click", () => {
      const expandedZoomLevel = this.supercluster.getClusterExpansionZoom(+cluster.id!)
      this.googleMap.setZoom(expandedZoomLevel)
      this.googleMap.panTo(marker.position!)
    })

    this.markers.set(key, marker)
  }

  private createPinMarker(key: string, pin: MapPin): void {
    if (this.markers.has(key)) {
      return
    }

    const marker = new SearchMapController.MarkerLibrary.AdvancedMarkerElement({
      content: pinMarkerContent(pin),
      map: this.googleMap,
      position: pin.position,
      title: pin.name,
      zIndex: Z_INDEX.PIN
    }) as AdvancedMarkerElement

    marker.addListener("click", () => {
      if (marker.content.classList.contains(ACTIVE_CLASS)) {
        return
      }

      selectContentEvent("Search Results", pin.name, { selectTarget: "Map marker" })

      // Activate clicked marker if it was previously deactivated
      this.activateMarker(marker, pin)
    })

    this.markers.set(key, marker)
  }

  private async fetchMarkers(): Promise<void> {
    const bounds = validBounds(this.googleMap.getBounds())

    if (!bounds) {
      return
    }

    if (this.isMapPanned) {
      const url = urlWithBounds(window.location.href, bounds)

      // Abort any previous fetch and reset controller.
      this.lookupAbort.abort()
      this.lookupAbort = new window.AbortController()

      try {
        this.loaderOutlet.startWithId(this.identifier)
        await renderTurboStream(url.toString(), { signal: this.lookupAbort.signal })
      } finally {
        this.loaderOutlet.stop()
      }
    } else {
      this.isMapPanned = true
    }
  }

  private async initGoogleMap(): Promise<void> {
    const { importLibrary } = window.google.maps
    const [coreLibrary, mapsLibrary, markerLibrary] = await Promise.all([
      importLibrary("core") as Promise<google.maps.CoreLibrary>,
      importLibrary("maps") as Promise<google.maps.MapsLibrary>,
      importLibrary("marker") as Promise<google.maps.MarkerLibrary>
    ])

    // Controller could potentially disconnect before libraries load (e.g. navigating away), so
    // return early to prevent map being initialized and trying to create markers with missing
    // DOM tenplate.
    if (!this.element.isConnected) {
      return
    }

    // Set static properties
    SearchMapController.LatLngBounds = coreLibrary.LatLngBounds
    SearchMapController.MarkerLibrary = markerLibrary

    // Initialize map and clusters
    this.googleMap = new mapsLibrary.Map(document.getElementById("js-google-map")!, {
      center: this.mapCenterValue,
      clickableIcons: false,
      disableDefaultUI: true,
      fullscreenControl: false,
      gestureHandling: "greedy", // Move map with one finger instead of two on mobile
      mapId: "dcee0ac2d8a57685", // https://developers.google.com/maps/documentation/javascript/map-ids/mapid-over
      maxZoom: 18,
      minZoom: 6,
      zoom: 6
    })
    this.supercluster = new Supercluster({ radius: 60 })

    this.appendCustomControls()

    // Wait until first `idle` before initial marker sync, and add listener for future `idle` events.
    window.google.maps.event.addListenerOnce(this.googleMap, "idle", () => {
      this.syncMarkers()

      this.googleMap.addListener("idle", this.fetchMarkersDebounce)
    })

    // Clicks on map should deactivate open markers.
    this.googleMap.addListener("click", this.deactivateMarker.bind(this))
  }

  private onViewportChange(): void {
    setSearchBarHeightForCSS()
  }

  private pointsInBounds(
    bounds: google.maps.LatLngBounds,
    zoom: number
  ): Array<ClusterFeature<AnyProps> | PointFeature<AnyProps>> {
    const { west, south, east, north } = bounds.toJSON()

    return this.supercluster.getClusters([west, south, east, north], zoom)
  }

  private recreateMarkers(): void {
    const bounds = this.googleMap.getBounds()
    const zoom = this.googleMap.getZoom()

    if (!bounds || zoom === undefined) {
      return
    }

    // Deactivate any active marker before setting new ones.
    this.deactivateMarker()

    const points = this.pointsInBounds(bounds, zoom)
    const markerKeys = new Set<string>()

    // Check each point and add either pin marker or cluster marker as appropriate.
    for (const point of points) {
      const key = markerKey(point)
      const { pin } = point.properties

      markerKeys.add(key)

      if (pin) {
        this.createPinMarker(key, pin)
      } else {
        this.createClusterMarker(key, point as ClusterFeature<AnyProps>)
      }
    }

    // Delete unused markers, removing click listener and map first.
    for (const [key, marker] of this.markers) {
      if (!markerKeys.has(key)) {
        window.google.maps.event.clearInstanceListeners(marker)
        marker.map = null
        this.markers.delete(key)
      }
    }
  }
}
